import { useCallback, useEffect, useState } from "react";

interface UseFormattingProps {
  editorRef: React.RefObject<HTMLDivElement | null>;
}

interface UseFormattingReturn {
  // State
  isBold: boolean;
  isItalic: boolean;
  isLink: boolean;
  isCode: boolean;
  isBlockquote: boolean;
  isHeading1: boolean;
  isHeading2: boolean;

  // Handlers
  toggleBold: () => void;
  toggleItalic: () => void;
  applyLink: (url: string) => void;
  removeLink: () => void;
  toggleCode: () => void;
  toggleBlockquote: () => void;
  toggleHeading1: () => void;
  toggleHeading2: () => void;
  updateFormatState: () => void;
  resetFormatState: () => void;
}

export const useFormatting = ({
  editorRef,
}: UseFormattingProps): UseFormattingReturn => {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isBlockquote, setIsBlockquote] = useState(false);
  const [isHeading1, setIsHeading1] = useState(false);
  const [isHeading2, setIsHeading2] = useState(false);

  // Execute format command
  const formatText = useCallback(
    (command: string, value = "") => {
      document.execCommand(command, false, value);
      if (editorRef.current) {
        editorRef.current.focus();
      }
    },
    [editorRef],
  );

  // Update format state based on current selection
  const updateFormatState = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let container = range.commonAncestorContainer;

      if (container.nodeType === 3) {
        container = container.parentElement as Node;
      }

      let currentNode: Node | null = container;
      let foundBold = false;
      let foundItalic = false;
      let foundLink = false;
      let foundCode = false;
      let foundBlockquote = false;
      let foundHeading1 = false;
      let foundHeading2 = false;

      while (currentNode && currentNode !== editorRef.current) {
        if (currentNode.nodeName === "B" || currentNode.nodeName === "STRONG") {
          foundBold = true;
        }
        if (currentNode.nodeName === "I" || currentNode.nodeName === "EM") {
          foundItalic = true;
        }
        if (currentNode.nodeName === "A") {
          foundLink = true;
        }
        if (currentNode.nodeName === "PRE") {
          foundCode = true;
        }
        if (currentNode.nodeName === "BLOCKQUOTE") {
          foundBlockquote = true;
        }
        if (currentNode.nodeName === "H1") {
          foundHeading1 = true;
        }
        if (currentNode.nodeName === "H2") {
          foundHeading2 = true;
        }
        currentNode = currentNode.parentNode;
      }

      setIsBold(foundBold);
      setIsItalic(foundItalic);
      setIsLink(foundLink);
      setIsCode(foundCode);
      setIsBlockquote(foundBlockquote);
      setIsHeading1(foundHeading1);
      setIsHeading2(foundHeading2);
    } else {
      setIsBold(false);
      setIsItalic(false);
      setIsLink(false);
      setIsCode(false);
      setIsBlockquote(false);
      setIsHeading1(false);
      setIsHeading2(false);
    }
  }, [editorRef]);

  // Format toggle functions
  const toggleBold = useCallback(() => {
    formatText("bold");
    updateFormatState();
  }, [formatText, updateFormatState]);

  const toggleItalic = useCallback(() => {
    formatText("italic");
    updateFormatState();
  }, [formatText, updateFormatState]);

  const applyLink = useCallback(
    (url: string) => {
      formatText("createLink", url);
      updateFormatState();
    },
    [formatText, updateFormatState],
  );

  const removeLink = useCallback(() => {
    formatText("unlink");
    updateFormatState();
  }, [formatText, updateFormatState]);

  const toggleCode = useCallback(() => {
    if (isCode) {
      formatText("formatBlock", "<div>");
    } else {
      formatText("formatBlock", "<pre>");
    }
    updateFormatState();
  }, [isCode, formatText, updateFormatState]);

  const toggleBlockquote = useCallback(() => {
    if (isBlockquote) {
      formatText("formatBlock", "<div>");
    } else {
      formatText("formatBlock", "<blockquote>");
    }
    updateFormatState();
  }, [isBlockquote, formatText, updateFormatState]);

  const toggleHeading1 = useCallback(() => {
    if (isHeading1) {
      formatText("formatBlock", "<div>");
    } else {
      formatText("formatBlock", "<h1>");
    }
    updateFormatState();
  }, [isHeading1, formatText, updateFormatState]);

  const toggleHeading2 = useCallback(() => {
    if (isHeading2) {
      formatText("formatBlock", "<div>");
    } else {
      formatText("formatBlock", "<h2>");
    }
    updateFormatState();
  }, [isHeading2, formatText, updateFormatState]);

  // Reset all format states
  const resetFormatState = useCallback(() => {
    setIsBold(false);
    setIsItalic(false);
    setIsLink(false);
    setIsCode(false);
    setIsBlockquote(false);
    setIsHeading1(false);
    setIsHeading2(false);
  }, []);

  // Listen for selection changes to update format state
  useEffect(() => {
    const handleSelectionChange = () => {
      if (document.activeElement === editorRef.current) {
        updateFormatState();
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [editorRef, updateFormatState]);

  return {
    // State
    isBold,
    isItalic,
    isLink,
    isCode,
    isBlockquote,
    isHeading1,
    isHeading2,

    // Handlers
    toggleBold,
    toggleItalic,
    applyLink,
    removeLink,
    toggleCode,
    toggleBlockquote,
    toggleHeading1,
    toggleHeading2,
    updateFormatState,
    resetFormatState,
  };
};
